/**
 * The data seam.
 *
 * Every screen speaks these types and nothing else. Two implementations exist:
 * the in-memory fake (`./fake.ts`), which is the data source in local
 * development and in tests, and the live adapter (`./generated-adapter.ts`),
 * which maps these shapes onto `@match-point/api-client-ts` — the client
 * generated from `packages/api-contract/openapi.yaml`. `./index.ts` picks one
 * from `VITE_DATA_SOURCE`.
 *
 * The seam is deliberately not a re-export of the generated types. The wire is
 * snake_case and nullable-by-marker; the app is camelCase and `Reported<T>`.
 * Keeping the translation in one adapter file means a contract change surfaces
 * as compile errors there rather than as behavior changes scattered through
 * eighteen screens — and it means the invariants below stay written as types
 * the whole app shares.
 *
 * ── What the types enforce ────────────────────────────────────────────────
 *
 * Two invariants are encoded structurally rather than trusted to review:
 *
 *   **I-4 — a provider's financial range reaches nobody but its owner.** There
 *   is no financial field anywhere on a type another org can read.
 *   `DirectoryResult` is the row a clinician sees and it has nowhere to put a
 *   price. `api.spec.ts` asserts that at compile time *and* scans the payload at
 *   runtime, because an excess property on an object literal is a compile error
 *   but a spread from a wider source is not.
 *
 *   **I-6 — insufficient data is a marker, never a zero.** Any number that can
 *   be absent is a `Reported<T>`, which has no numeric value to accidentally
 *   default to `0`. `formatReported` is the only renderer.
 *
 * There were three. The third was I-3 and I-5 — masking as a discriminated
 * union on `disclosure`, where the masked variant had no `contact` member to
 * read. ADR 0009 removed the referral plane, so the union has no subject. It is
 * worth knowing what the shape was, because it is the shape to reach for again
 * if anything reintroduces a payload passing between two organizations: the two
 * disclosure levels were two types, not one type with optional fields, so the
 * server's decision could not be second-guessed by a component.
 */

import type { Role } from '../auth/contract'
import type { components } from '@match-point/api-client-ts'

// ── Primitives ─────────────────────────────────────────────────────────────

/**
 * A value that may not have been reported.
 *
 * I-6, and DESIGN.md section 11: "No bed count reported renders a marker, not a
 * zero." A plain `number | null` invites `?? 0`; this shape does not have a
 * numeric slot to fall into.
 */
export type Reported<T> = { readonly reported: true; readonly value: T } | { readonly reported: false }

export const reported = <T>(value: T): Reported<T> => ({ reported: true, value })
export const unreported = <T>(): Reported<T> => ({ reported: false })

export interface Page<T> {
  readonly items: readonly T[]
  readonly page: number
  readonly pageSize: number
  readonly total: number
}

/** ISO 8601. Kept as a string so the seam has no date library in its contract. */
export type Timestamp = string

export interface PageQuery {
  page?: number
  pageSize?: number
}

/**
 * Failures the shared error state can reason about.
 *
 * `retryable` exists so one error component can decide whether to offer Retry
 * rather than every screen deciding. DESIGN.md section 10 wants retry on the
 * table screens and an explanation on the decision screens.
 */
export class ApiError extends Error {
  constructor (
    message: string,
    readonly detail: {
      status: number
      retryable: boolean
      /** Set when the server rejected specific fields. Rendered inline, never as a toast. */
      fields?: Readonly<Record<string, string>>
    }
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// ── Taxonomy — ORG-01 ──────────────────────────────────────────────────────

/**
 * The taxonomy kinds.
 *
 * Written out rather than aliased to the generated type, because this seam is
 * deliberately not a re-export — see the note at the top of this file. The
 * assertion below is what keeps that independence from becoming drift.
 *
 * **This was four values until migration 0012**, which took
 * `criteria_taxonomy.kind` to twenty-three after the provider forms were read.
 * Three separate places had to follow: the C# enum in `Platform.Contract`, the
 * enum in `openapi.yaml`, and this one. The first was caught by a test, the second
 * by regenerating the client, and this one only by `typecheck` — which is later
 * than it should have been, hence the assertion.
 */
export type TaxonomyKind =
  // The original four.
  | 'condition'
  | 'level_of_care'
  | 'amenity'
  | 'insurance'
  // Clinical, from the provider forms.
  | 'program_type'
  | 'population'
  | 'modality'
  | 'specialty'
  | 'substance'
  | 'medical_capability'
  // The half of every exclusion list that is not a diagnosis: recent violence,
  // criminal charges, a suicide attempt inside a time window, pregnancy, oxygen
  // dependence, a 24/7 nursing requirement.
  | 'admission_criterion'
  // Access and money.
  | 'payment_option'
  | 'language'
  | 'accessibility'
  // Operations.
  | 'admission_day'
  | 'intake_hours'
  // Sober living only. Housing has no insurance, no diagnoses and no modalities;
  // it has rules, rent and a sobriety requirement.
  | 'housing_rule'
  | 'housing_support'
  | 'housing_expectation'
  | 'acceptance_criterion'
  | 'sobriety_requirement'
  | 'mat_policy'
  // Utah Medicaid plans, plus two answers about taking Medicaid at all.
  | 'medicaid_plan'
  | 'staffing_model'
  // How a client attends a day-treatment program (migration 0028).
  | 'attendance'
  // A sober living house's deposit and minimum stay (migration 0030).
  | 'deposit_policy'
  | 'time_commitment'

/**
 * Compile-time proof that {@link TaxonomyKind} and the contract agree, in both
 * directions.
 *
 * A type-level assertion rather than an alias, so the seam keeps owning its own
 * types while a kind added to the contract and not here — or here and not there —
 * fails `typecheck` instead of surfacing as a screen that renders a term it does
 * not recognize. Nothing imports this; its only job is to fail.
 */
type ContractKind = components['schemas']['TaxonomyKind']

/**
 * True only when `A` and `B` are the same type, not merely mutually assignable.
 *
 * The function-signature form is deliberate. The obvious version —
 * `[A] extends [B] ? ([B] extends [A] ? true : never) : never` — resolves to
 * `never` on a mismatch, and a type alias equal to `never` is perfectly legal, so
 * it fails silently. That version was written here first and caught nothing:
 * adding an invented kind to the union below produced zero errors. This one
 * resolves to `false`, which `Expect` can reject.
 */
type Equals<A, B> =
  (<T>() => T extends A ? 1 : 2) extends (<T>() => T extends B ? 1 : 2) ? true : false

/** Fails to satisfy its own constraint when handed `false`. */
type Expect<T extends true> = T

/**
 * Compile-time proof that {@link TaxonomyKind} and the contract agree.
 *
 * Both directions matter and each is caught by something different. A kind added
 * to the contract and not here fails in `generated-adapter.ts`, where the wider
 * generated type stops being assignable. A kind added here and not to the contract
 * fails right below. Exported only so `noUnusedLocals` does not remove it; nothing
 * is meant to reference it.
 */
export type AssertTaxonomyKindsMatch = Expect<Equals<TaxonomyKind, ContractKind>>

/**
 * A resolved taxonomy entry, `{id, kind, label}` — the contract's
 * `TaxonomyRef`. Responses carry the resolved form so screens render without a
 * second lookup; requests carry bare ids, because a client-supplied label would
 * be a second, drifting source of truth.
 */
export interface TaxonomyTerm {
  readonly id: string
  readonly kind: TaxonomyKind
  readonly label: string
}

/**
 * The per-axis scores, minus financial.
 *
 * The contract carries `financial` as a `FinancialBand`, and ADR 0004 section 6
 * made it a band precisely so it is not invertible back to the provider's range.
 * It is still absent here: nothing in the slice's design renders it, and the
 * adapter drops it, so this app cannot show it even by accident. Widening this
 * type is a deliberate I-4 decision, not a plumbing change.
 */
export interface RankSubscores {
  readonly clinical: number | null
  readonly amenities: number | null
}

/**
 * The contract's `ReferralContact`: every member is value-or-null, because "we
 * looked and there is nothing" is a fact the receiver renders, not an absence
 * to infer. A card whose members are all null is still a revealed card.
 */
export interface RevealedContact {
  readonly name: string | null
  readonly phone: string | null
  readonly email: string | null
  readonly address: string | null
}

// ── Org — ORG-02..ORG-07 ───────────────────────────────────────────────────

/** `orgs.status`, matching the database CHECK. Only `approved` orgs are searchable. */
export type OrgStatus = 'pending' | 'approved' | 'rejected' | 'suspended'

/**
 * `org_profiles.approval_state`, matching the database CHECK — note
 * `info_requested`, not `information_requested`; two names for one value is
 * how a translation bug gets written. The four non-draft states are the ones
 * `ORG-05` renders distinctly.
 */
export type ApprovalState = 'draft' | 'pending' | 'approved' | 'rejected' | 'info_requested'

/**
 * A company's own published links (`ORG-02`): its website and its social
 * pages. Public directory data by design.
 *
 * No phone and no email (ADR 0014). A clinician calls a *program*, and each
 * program publishes its own admissions phone; a company number beside it would
 * be a second number for the same call, and the one least likely to reach
 * intake.
 */
export interface OrgPublicContact {
  readonly websiteUrl?: string
  /**
   * The **company's** page, not a program's.
   *
   * The residential form asked Website, Facebook and Instagram per program. A
   * company does not have a different Instagram for each of its buildings, and
   * the same fact asked once per building is a fact three answers can disagree
   * about, so the three live here.
   */
  readonly facebook?: string
  readonly instagram?: string
}

/** Most highlights a company page carries — `OrgHighlights.maxItems`. */
export const ORG_HIGHLIGHTS_MAX = 8
/** Longest single highlight — `OrgHighlights.items.maxLength`. */
export const ORG_HIGHLIGHT_MAX_LENGTH = 80

/**
 * One city a company operates in, derived server-side from its **approved**
 * programs and every house of each (ADR 0014). Read-only: a provider changes
 * it by editing a program, never on the company page.
 */
export interface OrgLocation {
  readonly city: string
  /** Two-letter US state or territory code. */
  readonly region: string
}

/** One program's stated age range. Either bound is null when left open — "18+" is `{min: 18, max: null}`. */
export interface OrgAgeRange {
  readonly min: number | null
  readonly max: number | null
}

/**
 * What a company's programs offer, rolled up — the contract's
 * `OrgProgramSummary` (ADR 0014 decision 5). Derived server-side on every read
 * from the company's **approved** programs, never stored and never edited on the
 * company. A union, not a promise: "Medicaid" here means *some* program takes it,
 * and each listing still answers for itself.
 *
 * Never a held-back input (I-4): exclusions, amenities and price are not here.
 */
export interface OrgProgramSummary {
  readonly programCount: number
  readonly levelsOfCare: readonly TaxonomyTerm[]
  /** Each distinct range some approved program states, sorted by lower bound. */
  readonly ageRanges: readonly OrgAgeRange[]
  /** In-network carriers only. */
  readonly insuranceInNetwork: readonly TaxonomyTerm[]
  readonly populations: readonly TaxonomyTerm[]
  readonly diagnoses: readonly TaxonomyTerm[]
}

/**
 * Bed and seat counts, as a program reports them. The one part of a listing
 * exempt from the I-8 approval gate. `null` is "never reported", which renders
 * as a marker (I-6). The name predates ADR 0014, which took beds off the
 * company page; only programs carry them now.
 */
export interface OrgSeats {
  readonly total: number | null
  readonly open: number | null
}

/**
 * The organization's picture (`ORG-02`).
 *
 * An absolute URL, resolved by the server against the origin the objects are
 * served from, so no client holds a base URL of its own. Safe to use directly
 * as an `<img src>`.
 *
 * The URL is unguessable and it is not secret — whoever holds it can fetch the
 * object. What the I-8 gate controls is who is *told* it: a directory reader
 * gets the approved picture and the owner gets the latest one, because those
 * are different rows. A picture awaiting review is undisclosed, not
 * unreachable.
 */
export interface OrgImage {
  readonly url: string
  readonly contentType: OrgImageContentType
}

/**
 * The three formats a provider may upload.
 *
 * SVG is absent and must stay absent: it is a document that executes script,
 * and serving one from the origin the app is on would be stored cross-site
 * scripting. The server enforces the same list twice — once for the error a
 * provider can read, once as a database `CHECK`.
 */
export type OrgImageContentType = 'image/png' | 'image/jpeg' | 'image/webp'

/** Whether a browser-reported file type is one this product accepts. */
export function isOrgImageContentType (value: string): value is OrgImageContentType {
  return value === 'image/png' || value === 'image/jpeg' || value === 'image/webp'
}

/**
 * A write grant for one object, from `POST /orgs/{id}/profile-image-uploads`.
 *
 * The browser posts the file straight to S3 with no credentials and no
 * `Authorization` header: `fields` carries the policy and its signature, and
 * every entry is covered by that signature. Send them all, unedited, ahead of
 * the file.
 *
 * `maxBytes` is what the policy will accept. It is not the enforcement — S3
 * refuses a longer body whatever this client believes — but a client that
 * checks it first can say "that image is too large" in the app's own words
 * instead of surfacing an XML error document.
 */
export interface OrgImageUpload {
  readonly mediaId: string
  readonly uploadUrl: string
  readonly fields: Readonly<Record<string, string>>
  readonly maxBytes: number
  readonly contentType: OrgImageContentType
  readonly expiresAt: Timestamp
}

/**
 * The owning org's view of its own profile — the **latest** version, pending
 * draft included, so `ORG-03`'s editor loads what was last submitted rather
 * than what was last approved. `approvalState` + `pendingFields` are
 * `ORG-05`'s banner.
 *
 * General company information only (ADR 0014): beds, address, criteria and
 * price range belong to programs.
 */
export interface OwnOrgProfile {
  readonly view: 'owner'
  readonly orgId: string
  readonly name: string
  /** `pending` renders the approval-wait screen, not an empty inbox. */
  readonly orgStatus: OrgStatus
  readonly approvalState: ApprovalState
  readonly version: number
  /** JSON Pointers into this document: `/description`, `/highlights`, `/contact`, `/image`. */
  readonly pendingFields: readonly string[]
  readonly description: string | null
  /** Short public lines shown as bullets. Empty when none. */
  readonly highlights: readonly string[]
  /** The latest picture — the pending one while a version is in review. */
  readonly image: OrgImage | null
  readonly contact: OrgPublicContact | null
  /**
   * Derived from the **approved** programs, not the drafts — exactly what the
   * directory shows. Read-only; empty when no program is approved yet.
   */
  readonly locations: readonly OrgLocation[]
  /** The approved programs, rolled up. Read-only, like `locations`. */
  readonly programSummary: OrgProgramSummary
  readonly updatedAt: Timestamp
}

/** The published directory view — the approved version only, or nothing. */
export interface OrgDirectoryView {
  readonly view: 'directory'
  readonly orgId: string
  readonly name: string
  readonly description: string | null
  readonly highlights: readonly string[]
  /** The approved picture, or null when the provider publishes none. */
  readonly image: OrgImage | null
  readonly contact: OrgPublicContact | null
  /** Every city the company's approved programs operate in, by state then city. */
  readonly locations: readonly OrgLocation[]
  /** The approved programs, rolled up. Read-only, like `locations`. */
  readonly programSummary: OrgProgramSummary
  readonly updatedAt: Timestamp
}

/**
 * What `GET /orgs/{id}` returns: the owner's own org answers `owner`, a staff
 * reviewer reading any org also answers `owner`, and everyone else answers
 * `directory`. Narrow on `view` before touching `approvalState` — a directory
 * reader has no business knowing whether someone else's profile has an edit in
 * review.
 *
 * `view` names the document, not the reader. `owner` means "the latest version
 * with its review state attached", which is what an owner loads into the editor
 * and what a reviewer loads to decide. Do not read it as "this is mine" —
 * `getOrgProfile` is the call that means that.
 */
export type OrgView = OwnOrgProfile | OrgDirectoryView

/**
 * A partial edit (`PATCH /orgs/{id}`). An absent property means "leave it
 * alone"; there is no null-to-clear. `highlights` replaces the whole list, and
 * `[]` clears it.
 */
export interface UpdateOrgProfileInput {
  description?: string
  highlights?: readonly string[]
  /**
   * Exactly one of the two. `remove` is a word rather than a null because an
   * absent property already means "leave it alone" everywhere in this type —
   * the picture is the only field a provider can want to genuinely clear.
   */
  image?: { mediaId: string } | { remove: true }
  contact?: OrgPublicContact
}

/**
 * Human labels for the JSON Pointers `pendingFields`, `appliedImmediately` and
 * `pendingReview` carry. An unknown pointer falls back to itself rather than
 * hiding — a new server field showing up as `/new_thing` is information, and
 * an omitted one would be a silent lie about what is in review.
 */
export function pendingFieldLabel (pointer: string): string {
  const labels: Record<string, string> = {
    '/description': 'About your company',
    '/highlights': 'Program highlights',
    '/image': 'Profile picture',
    '/contact': 'Online presence'
  }
  return labels[pointer] ?? pointer
}

/**
 * What happened to each field you sent — I-8's split, made explicit.
 * `appliedImmediately` is only ever `/image`, a picture removal; everything
 * else lands in `pendingReview` and waits for staff. JSON Pointers,
 * same vocabulary as `pendingFields`.
 */
export interface ProfileEditReceipt {
  readonly approvalState: ApprovalState
  readonly version: number
  readonly appliedImmediately: readonly string[]
  readonly pendingReview: readonly string[]
}

// ── Home — CLI-05 ──────────────────────────────────────────────────────────

/**
 * `CLI-05` is role-aware. There is no `/home` endpoint — both implementations
 * compose the payload per role from the calls that do exist, with panels as a
 * list so one panel can fail while the others render (DESIGN.md section 10's
 * partial state for Home).
 */
export interface HomePayload {
  readonly role: Role
  readonly greetingName: string
  readonly panels: readonly HomePanel[]
  /** Panels that could not be built. Rendered as panel-level errors. */
  readonly failedPanelTitles: readonly string[]
}

export interface HomePanel {
  readonly id: string
  readonly title: string
  readonly kind: 'metric' | 'list' | 'actions'
  readonly metric?: Reported<number>
  readonly metricCaption?: string
  readonly items?: readonly { readonly id: string; readonly label: string; readonly meta: string; readonly to?: string }[]
  readonly actions?: readonly { readonly label: string; readonly to: string }[]
}

/**
 * The facts Home is composed from. Each is `Reported` because each comes from
 * its own call, any of which can fail or genuinely have no value — and a failed
 * count must render as a failed panel or a marker, never as `0`.
 */
export interface HomeFacts {
  /**
   * Listings the account owns. Replaced `clientCount` and `undecidedInbox`,
   * which ADR 0009 removed the endpoints for — a panel whose number no shipped
   * endpoint can answer is the thing the note below exists to prevent.
   */
  readonly programCount: Reported<number>
  readonly bedsOpen: Reported<number>
  readonly pendingAccounts: Reported<number>
  /**
   * Submitted profile versions awaiting a decision. Its own panel rather than
   * folded into `pendingAccounts`, because the two counts move independently: a
   * cleared account queue said "nothing to do" while approved providers' edits
   * sat unreviewed indefinitely, which is the state this panel exists to make
   * impossible to miss.
   */
  readonly pendingProfiles: Reported<number>
  readonly failedPanelTitles: readonly string[]
}

/**
 * One composition, shared by the fake and the live adapter, so dev mode and
 * production show the same Home. The panel set is what the shipped endpoints
 * can actually answer — a panel only the fake could build would teach the screen
 * to expect a number that production has no way to produce.
 */
export function composeHomePayload (role: Role, greetingName: string, facts: HomeFacts): HomePayload {
  const panels: Record<Role, readonly HomePanel[]> = {
    provider_admin: [
      {
        id: 'programs',
        title: 'Programs you have listed',
        kind: 'metric',
        metric: facts.programCount,
        metricCaption: 'one per facility'
      },
      {
        id: 'beds',
        title: 'Beds open',
        kind: 'metric',
        // I-8's exception, and I-6's marker: an org that has never reported a
        // count must not render a zero.
        metric: facts.bedsOpen,
        metricCaption: 'updates immediately, no approval needed'
      },
      {
        id: 'actions',
        title: 'Keep your listing accurate',
        kind: 'actions',
        actions: [
          { label: 'Your programs', to: '/org/programs' },
          { label: 'Edit your company page', to: '/org/profile' }
        ]
      }
    ],
    mp_admin: [
      {
        id: 'queue',
        title: 'Accounts awaiting approval',
        kind: 'metric',
        metric: facts.pendingAccounts,
        metricCaption: 'organizations in the queue'
      },
      {
        // Two counts, not one. An approved organization editing its listing
        // never re-enters the account queue, so a staff member reading only the
        // panel above would see "queue clear" while submitted profile changes
        // waited with nobody looking at them.
        id: 'profile-queue',
        title: 'Profile changes awaiting review',
        kind: 'metric',
        metric: facts.pendingProfiles,
        metricCaption: 'submitted versions in the queue'
      },
      {
        id: 'actions',
        title: 'Staff surfaces',
        kind: 'actions',
        actions: [
          { label: 'Review the approval queue', to: '/staff/approvals' },
          { label: 'Open the audit log', to: '/staff/audit' }
        ]
      }
    ]
  }

  return {
    role,
    greetingName,
    panels: panels[role],
    failedPanelTitles: facts.failedPanelTitles
  }
}

// ── Staff — ORG-06, reduced ORG-07 ─────────────────────────────────────────

/** One row of the `ORG-06` queue — the contract's `AccountForReview`. */
export interface PendingAccount {
  readonly orgId: string
  readonly orgName: string
  readonly status: OrgStatus
  /** `mp_admin` is absent because nobody can request it. */
  readonly submittedAt: Timestamp
}

export interface AccountQueueQuery extends PageQuery {
  /** Defaults to `pending` server-side — the common case is "what needs deciding". */
  status?: OrgStatus
}

/**
 * `ORG-06`. The note is required on both outcomes: an unexplained denial is
 * the one a founder gets asked to justify. Written to the hash-chained audit
 * log.
 */
export interface AccountDecisionInput {
  decision: 'approve' | 'deny'
  note: string
}

export interface AccountDecisionResult {
  readonly orgId: string
  readonly status: OrgStatus
  readonly decidedAt: Timestamp
}

/**
 * One row of the profile review queue — the contract's `ProfileForReview`.
 *
 * A distinct queue from `PendingAccount` and not a variation of it. That one
 * filters `orgs.status` and answers "whose account is undecided"; this one
 * filters `org_profiles.approval_state` and answers "whose submitted profile is
 * undecided". An organization approved months ago never reappears in the first
 * and appears in the second every time it edits its listing — which is the case
 * that had no surface at all before this existed.
 */
export interface PendingProfile {
  readonly orgId: string
  readonly orgName: string
  /** Where the *account* sits. A different decision from the one this row is for. */
  readonly orgStatus: OrgStatus
  readonly version: number
  readonly approvalState: ApprovalState
  /**
   * The highest approved version this org currently has, or `null` when it has
   * never had one.
   *
   * `null` is a provider not in the directory at all, whose whole listing is
   * waiting. A number is an organization already taking referrals whose *edit*
   * is waiting. Same delay, two different harms, and the queue labels them
   * differently rather than making a reviewer work it out.
   */
  readonly liveVersion: number | null
  /**
   * When this version was opened. Deliberately not its last-updated stamp: a
   * seat-count change rewrites every version of a profile, and sorting on that
   * would let a provider reorder the review queue by editing a bed count.
   */
  readonly submittedAt: Timestamp
}

export interface ProfileQueueQuery extends PageQuery {
  /** Defaults to `pending` server-side — the common case is "what needs deciding". */
  state?: ApprovalState
}

/** Reduced `ORG-07`: decide the latest pending profile version. */
export interface ProfileDecisionInput {
  decision: 'approve' | 'reject' | 'request_information'
  note: string
}

export interface ProfileDecisionResult {
  readonly orgId: string
  readonly version: number
  readonly approvalState: ApprovalState
  readonly decidedAt: Timestamp
}

/**
 * One row of the *program* review queue — the contract's `ProgramForReview`.
 *
 * A third queue, and not a variation of the other two. `PendingAccount` asks
 * whose account is undecided and `PendingProfile` whose organization profile is;
 * this one asks whose **listing** is. ADR 0007 makes the program the listed
 * entity, so a company with three facilities puts three rows here and a reviewer
 * can approve two of them.
 */
/** Where to look. Every property is optional and they intersect. */
export interface DirectoryLocation {
  /** Two-letter state code. */
  region?: string
  /** Matched case-insensitively. */
  city?: string
  /**
   * Five digits. Populates `distanceMiles` and makes `distance` sortable.
   * Excludes nothing on its own; a code with no centroid is a `400`.
   */
  originPostalCode?: string
  /** Excludes anything farther. Requires an origin. One of the closed set. */
  radiusMiles?: DirectoryRadius
}

/**
 * The radii the server accepts, widest last.
 *
 * A closed set rather than a free number: centroid distance does not support the
 * precision an arbitrary integer implies, and "widen the radius" needs a next
 * value to widen to.
 *
 * Starts at 25. A 10-mile step was offered and removed — a straight-line
 * centroid-to-centroid measurement is least trustworthy exactly where the radius
 * is tightest. Clearing the control is "any distance", which sends no radius.
 */
export type DirectoryRadius = 25 | 50 | 100 | 300

export const DIRECTORY_RADII: readonly DirectoryRadius[] = [25, 50, 100, 300]

/**
 * The most ids one taxonomy axis of a search may name — `maxItems: 20` on every
 * list in `DirectorySearchRequest`, and `MaximumTagsPerAxis` on the server.
 *
 * Restated here because a screen that offers more than the server accepts turns
 * the twenty-first pick into a 400 the reader did nothing to deserve. The
 * additional-needs box offered twenty-one before this existed.
 */
export const DIRECTORY_MAX_PER_AXIS = 20

/**
 * What each search control may offer, from `GET /directory/filters`.
 *
 * Narrower than a taxonomy kind, and not derivable here. A term is offerable
 * when some provider form can tag a program with it, which only the server
 * knows: the taxonomy was seeded ahead of the forms that will use it, so most
 * of kind `condition` is vocabulary no program can carry yet. The screen used
 * to build these lists by filtering the taxonomy by kind, which is why a
 * clinician could pick Bipolar disorder and read an empty page as "nobody
 * treats this".
 *
 * Four lists rather than one grouped by kind, because which axis a term serves
 * is a server decision — it follows from the question that aliases to it.
 */
export interface DirectoryFilters {
  readonly programTypes: readonly TaxonomyTerm[]
  readonly diagnoses: readonly TaxonomyTerm[]
  readonly insurance: readonly TaxonomyTerm[]
  readonly specialties: readonly TaxonomyTerm[]
  /** What a program can offer at its location — PHP, IOP and the rest (migration `0028`). */
  readonly levelsOfCare: readonly TaxonomyTerm[]
  /** In-Person, Virtual, Hybrid. */
  readonly attendance: readonly TaxonomyTerm[]
  /**
   * The same lists once per program type, in `programTypes` order, narrowed to
   * what that type's form asks. The search asks for a type first and offers
   * only these, because a filter the type's form never asks hides every program
   * of that type for want of an answer.
   */
  readonly byProgramType: readonly ProgramTypeFilters[]
}

/** What one program type's form lets a search filter on. */
export interface ProgramTypeFilters {
  readonly programType: TaxonomyTerm
  readonly diagnoses: readonly TaxonomyTerm[]
  readonly insurance: readonly TaxonomyTerm[]
  readonly specialties: readonly TaxonomyTerm[]
  readonly levelsOfCare: readonly TaxonomyTerm[]
  readonly attendance: readonly TaxonomyTerm[]
  /** Whether the form asks the ages a program serves. */
  readonly ages: boolean
  /** Whether some level on the form has a schedule. */
  readonly sessionTimes: boolean
  /** Whether some level on the form has a length in weeks. */
  readonly lengthWeeks: boolean
}

/**
 * When a track meets, as the directory reads it: a track starting at 16:00 or
 * later is `evening`. Derived on the server and never stored, so a label and
 * the hours cannot disagree.
 */
export type DirectorySessionTime = 'daytime' | 'evening'

/** The hour, as `HH:MM`, at which a track counts as an evening track. */
export const EVENING_FROM = '16:00'

/** Nearest first, or alphabetical. No fit order — the directory does not score. */
export type DirectorySort = 'distance' | 'name'

/**
 * `DIR-01`'s filters. Every one is optional; none of them scores anything.
 *
 * The axes do not all combine the same way, and a screen has to say so: program
 * types and insurance are any-of, populations and diagnoses are all-of, and
 * admission criteria are the only one phrased as an exclusion.
 */
export interface DirectoryQuery extends PageQuery {
  /** Kind `program_type`. Any of them matches. */
  programTypes?: readonly string[]
  location?: DirectoryLocation
  /**
   * One company's listed programs, which is what the company page renders.
   *
   * Reusing the search rather than a dedicated endpoint keeps one implementation
   * of the two visibility gates — an approved listing and an approved account.
   */
  orgId?: string
  /** Kind `insurance`. Any of them matches. */
  insurance?: readonly string[]
  /**
   * Defaults to `any` server-side. `in_network` is usually what a client needs,
   * but defaulting to it would hide programs a self-paying client can use.
   */
  insuranceNetwork?: DirectoryNetwork
  /** Kind `population`. **All** of them must match. */
  populations?: readonly string[]
  /** Kind `condition`. **All** of them must be accepted. */
  diagnoses?: readonly string[]
  /**
   * Kind `admission_criterion`. **None** of them may be auto-declined.
   *
   * Things true of a client that a program refuses on rather than treats. The
   * only axis phrased as an exclusion, because no program lists "accepts a client
   * with pending charges".
   */
  admissionCriteria?: readonly string[]
  /**
   * Kind `specialty`. **All** of them must be offered.
   *
   * What the provider form calls Treatment Specialties and the search asks for
   * as additional needs. Not a second spelling of `diagnoses`: the two kinds
   * overlap in wording and neither contains the other, because a specialty is a
   * capability a program advertises and a condition is a diagnosis it accepts.
   */
  specialties?: readonly string[]
  /** The youngest client to be placed. Sent with `ageMax` or alone. */
  ageMin?: number
  /**
   * The oldest client to be placed. `ageMin` above `ageMax` is a `400`.
   *
   * Overlap, not containment: a program matches when its published range shares
   * any age with this one. A program that published neither end is dropped and
   * counted in `excludedWithoutAges` — never read as zero, never as unbounded.
   * One end published is an answer and does match.
   */
  ageMax?: number
  /**
   * Kind `level_of_care`. Any of them matches.
   *
   * The schedule filters below read the same level: a program matches only when
   * **one** level it offers is in this list, meets at the times asked and runs
   * no longer than asked. An evening IOP and a short PHP from the same program
   * are not an evening program that runs six weeks.
   */
  levelsOfCare?: readonly string[]
  /** Any of them matches. A level with no schedule on file does not. */
  sessionTimes?: readonly DirectorySessionTime[]
  /** The longest program the client can commit to. An unstated length does not match. */
  maxLengthWeeks?: number
  /** Kind `attendance`. Any of them matches; `Hybrid` is its own answer. */
  attendance?: readonly string[]
  /** Defaults to `distance` when an origin was given, `name` otherwise. */
  sort?: DirectorySort
}

export type DirectoryNetwork = 'any' | 'in_network'

/** One program in the directory. Thin — enough to decide whether to open it. */
export interface DirectoryResult {
  readonly programId: string
  readonly name: string
  readonly programType: string
  /** The company. One company runs many programs, which a list has to show. */
  readonly orgId: string
  readonly orgName: string
  readonly city: string | null
  readonly region: string | null
  /**
   * Straight-line miles between postal-code centroids, or `null`.
   *
   * `null` means "we could not measure it" — no origin was given, or the program
   * published no postal code. Render a marker, never a `0`: a facility shown at
   * zero miles is the top of the page.
   */
  readonly distanceMiles: number | null
  /**
   * What the distance was measured from, or `null` when it was not measured.
   *
   * A ZIP centroid is accurate to about the size of a ZIP code; a city centroid
   * to about the size of a city, which in the West is a very different number.
   * Render the difference — a screen that shows both as plain mileage invites a
   * clinician to trust the looser one the way they trust the tighter one.
   */
  readonly distanceBasis: 'postal_code' | 'city' | null
  /**
   * How this program takes the insurance that was asked for, or `null` when the
   * search named none. The difference between affordable and not.
   */
  readonly insuranceNetwork: 'in_network' | 'out_of_network' | null
  /** The youngest client it takes, or `null` when unpublished or open. */
  readonly ageMin: number | null
  /** The oldest client it takes, or `null` when unpublished or open. */
  readonly ageMax: number | null
  /** The admissions line as typed, so a result can be called from the list. */
  readonly admissionsPhone: string | null
  /** The company's approved logo, or `null` when it publishes none. */
  readonly orgImageUrl: string | null
  /** Specialty labels, alphabetical. Empty when it named none. */
  readonly specialties: readonly string[]
  /**
   * How many houses the program lists, the first included (ADR 0013).
   * `city`, `region` and `distanceMiles` describe one of them — the nearest
   * when the search had an origin — so a card can say which house is that far.
   */
  readonly houseCount: number
}

/** A page of the directory. */
export interface DirectoryResults extends Page<DirectoryResult> {
  /**
   * How many programs met every other filter and were dropped by the radius
   * because nothing could place them. `null` when no radius was applied.
   *
   * Since `DIR-04` this means no postal code *and* no city the gazetteer knows,
   * which a free-text field makes reachable. A screen has to render it even at
   * zero results, which is when it matters: without it a clinician cannot tell
   * "nothing is nearby" from "nothing nearby could be placed".
   */
  readonly excludedWithoutLocation: number | null
  /**
   * How many programs survived the radius and were then dropped by the age range
   * because the provider published neither end of theirs. `null` when no age was
   * asked for.
   *
   * The same fact as `excludedWithoutLocation`, one filter over, and it has to
   * be rendered at zero results for the same reason: an empty page because
   * nobody answered is not an empty page because nobody qualifies. Expect this
   * to be large — the provider form only started asking in migration `0021`.
   */
  readonly excludedWithoutAges: number | null
}

export interface PendingProgram {
  readonly programId: string
  readonly name: string
  readonly programType: string
  readonly orgId: string
  readonly orgName: string
  /** Where the *account* sits. A separate gate: approving here publishes nothing on its own. */
  readonly orgStatus: OrgStatus
  readonly version: number
  readonly approvalState: ApprovalState
  /**
   * The highest approved version this program has, or `null` when it has never
   * had one.
   *
   * `null` is a facility not in the directory at all, whose whole listing is
   * waiting. A number is a facility already taking referrals whose *edit* is
   * waiting. Same delay, two different harms, and the queue labels them rather
   * than making a reviewer work it out.
   */
  readonly liveVersion: number | null
  readonly city: string | null
  readonly region: string | null
  /**
   * When this version was opened. Deliberately not its last-updated stamp: a
   * bed-count change rewrites the row, and sorting on that would let a provider
   * reorder the review queue.
   */
  readonly submittedAt: Timestamp
}

export interface ProgramQueueQuery extends PageQuery {
  /** Defaults to `pending` server-side — the common case is "what needs deciding". */
  state?: ApprovalState
}

/**
 * `PRG-10`: decide a program's latest pending version.
 *
 * Approving is the only thing that promotes a provider's staged selections into
 * the set the directory filters on. Nothing a provider does reaches it.
 */
export interface ProgramDecisionInput {
  decision: 'approve' | 'reject' | 'request_information'
  note: string
}

export interface ProgramDecisionResult {
  readonly programId: string
  readonly version: number
  readonly approvalState: ApprovalState
  readonly decidedAt: Timestamp
}

// ── Session context ────────────────────────────────────────────────────────

/**
 * Who is signed in, as far as a data implementation needs to know.
 *
 * Getters rather than values so an implementation never captures a stale
 * session across the dev role switch or a token refresh. The org id addresses
 * `GET /orgs/{id}` for the caller's own profile, and the role composes Home.
 *
 * Neither uses it to hide the caller's own listings from the directory, and that
 * is deliberate: `SRCH-02`'s scored search excluded the searcher's own org, and
 * ADR 0007 decision 4 reverses the rule for the directory — the only way to
 * check how your own listing reads is to read it where a clinician does.
 */
export interface SessionContext {
  currentOrgId: () => string | null
  currentRole: () => Role | null
  currentDisplayName: () => string
}

// ── Idempotency ────────────────────────────────────────────────────────────

/**
 * A fresh idempotency key. One per user-initiated submit: a transport retry of
 * that submit reuses the key and replays the original response, while a new
 * submit — the user correcting a typo and pressing the button again — needs a
 * new one. The wire format is a UUID (`Idempotency-Key`, required on every
 * mutating operation).
 */
export function newIdempotencyKey (): string {
  return globalThis.crypto.randomUUID()
}

// ── The interface ──────────────────────────────────────────────────────────

/**
 * Every server call the shell makes. Deliberately narrow: it covers the screens
 * in ADR 0004's first slice and nothing else, because a method here that no
 * screen calls is a guess about an endpoint that has not been written.
 *
 * Mutations take an idempotency key — one per user-initiated submit, reused
 * only for transport retries of that same submit. Lambda retries are a
 * certainty and a duplicated referral is a trust failure (spec section 6).
 */

// ── Programs — ADR 0007 ────────────────────────────────────────────────────

/**
 * One choice a select question offers.
 *
 * `label` is what **this form** displays and `taxonomyId` is what it resolves to.
 * Two questions can show different labels for one id — the residential form
 * spells an insurer `Blue Cross Blue Shield` under its in-network question and
 * `BCBS` under its out-of-network one — so a screen renders the label and posts
 * the id, never the other way round.
 */
export interface ProgramFormOption {
  readonly taxonomyId: string
  readonly label: string
  /**
   * Staff have stopped offering this choice (ADR 0015). Served so a program
   * that already holds it can still show the box it ticked; a screen shows it
   * only when it is selected, and the save refuses it as a new pick.
   */
  readonly retired?: true
}

// ── Staff option editor (ADM-08, ADR 0015) ─────────────────────────────────

/** One select question, as the staff option editor lists it. */
export interface ManagedQuestion {
  readonly key: string
  readonly question: string
  /** The title of the wizard step it sits on. */
  readonly section: string
  readonly inputType: 'single_select' | 'multi_select'
  readonly optionCount: number
  readonly retiredCount: number
}

/**
 * Which staff review queues email the caller when work arrives (ADR 0017).
 * Email only: texts are the second phase, and a switch for a channel that
 * cannot send would be a promise the product breaks.
 */
export interface StaffNotificationPreferences {
  readonly email: {
    readonly account: boolean
    readonly profile: boolean
    readonly program: boolean
  }
}

export interface ManagedForm {
  readonly source: string
  readonly title: string
  readonly questions: readonly ManagedQuestion[]
}

/** One choice on one question, with what staff need to decide about it. */
export interface ManagedOption {
  readonly taxonomyId: string
  /** This form's wording. Editable. */
  readonly label: string
  /** What the directory's filters show. Fixed: show-when rules match on it. */
  readonly canonicalLabel: string
  readonly kind: TaxonomyKind
  readonly retired: boolean
  /** Why it cannot be retired, or null when it can. */
  readonly protectedReason: string | null
  /** Listed programs on this form whose live criteria include it. */
  readonly programCount: number
}

export interface ManagedQuestionOptions {
  readonly source: string
  readonly key: string
  readonly question: string
  /** The kinds a new choice may take. More than one means staff pick. */
  readonly kinds: readonly TaxonomyKind[]
  /** True when the question keeps its own order rather than the alphabet. */
  readonly ordered: boolean
  /** Other forms asking a question with the same key. */
  readonly alsoAskedOn: readonly string[]
  /** Every choice, retired included, in the order the wizard offers them. */
  readonly options: readonly ManagedOption[]
}

export interface AddFormOptionInput {
  readonly label: string
  readonly kind?: TaxonomyKind
  readonly alsoSources?: readonly string[]
}

export interface UpdateFormOptionInput {
  readonly label?: string
  readonly retired?: boolean
}

/** One question on a provider form. */
export interface ProgramFormQuestion {
  /** Stable machine name. Answers are posted under this. */
  readonly key: string
  /** The form's exact wording. Never paraphrased by a screen. */
  readonly question: string
  readonly inputType:
    | 'short_text'
    | 'long_text'
    | 'email'
    | 'phone'
    | 'url'
    | 'single_select'
    | 'multi_select'
    // Migration 0028. A whole number is answered under `text` as digits; a
    // schedule under `schedules`.
    | 'whole_number'
    | 'weekly_schedule'
    // ADR 0013: a program's further houses, answered under `locations`.
    | 'location_list'
  readonly required: boolean
  readonly helper: string | null
  /**
   * Empty for free-text questions.
   *
   * `Other` is pinned last by the server wherever the alphabet would have put
   * it: it is the escape hatch rather than a choice among peers.
   */
  readonly options: readonly ProgramFormOption[]
  /**
   * What has to be ticked before this question is shown, or `null` when it is
   * always shown.
   *
   * Five questions on the residential form offer an `Other` option, and ticking
   * it opens a free-text field so a provider can say what they meant. A screen
   * renders the question when the named question's selection contains the named
   * id, and knows nothing about which questions these are — the hospital form
   * will have its own.
   *
   * Optional rather than nullable, because absent and null would mean the same
   * thing here — always shown — and the wire omits the key entirely.
   */
  readonly revealedBy?: ProgramFormReveal
}

export interface ProgramFormReveal {
  readonly questionKey: string
  /** An id rather than a label, so no screen is a place `Other` can be misspelled. */
  readonly taxonomyId: string
}

/** One step of the wizard. */
export interface ProgramFormSection {
  readonly key: string
  readonly title: string
  readonly helper: string | null
  readonly questions: readonly ProgramFormQuestion[]
}

/**
 * A whole onboarding form, served from the database.
 *
 * **No screen hardcodes any of this.** Five forms exist and a sixth would cost a
 * seed rather than a release, which only holds while the wizard renders whatever
 * arrives and adds no questions of its own.
 */
export interface ProgramFormDefinition {
  readonly source: string
  readonly title: string
  readonly estimate: string | null
  readonly sections: readonly ProgramFormSection[]
}

/** One program in a provider's own list. */
export interface ProgramListRow {
  readonly id: string
  readonly name: string
  readonly programType: string
  readonly formSource: string | null
  readonly approvalState: ApprovalState
  readonly version: number
  /** Sections saved at least once, for progress without loading every answer. */
  readonly sectionsComplete: readonly string[]
  readonly city: string | null
  readonly region: string | null
  /**
   * The first house's bed counts. `null` is "never reported", which renders as
   * a marker.
   */
  readonly seats: OrgSeats
  /** Further houses, each with its own beds (ADR 0013). Empty for one house. */
  readonly houses: readonly ProgramHouseRow[]
}

/** `PRG-11`: either count or both. An absent one keeps its value. */
export interface ProgramSeatsInput {
  readonly total?: number
  readonly open?: number
  /** A further house's id (ADR 0013). Absent for the program's first house. */
  readonly locationId?: string
}

/**
 * What changing a program's beds did. `approvalState` and `version` are the
 * latest version's and are unchanged: this is the one program write that is
 * not reviewed, and a screen can show that nothing went to review.
 */
export interface ProgramSeatsReceipt {
  readonly seats: OrgSeats
  readonly approvalState: ApprovalState
  readonly version: number
}

/** Where a program is. Every field optional, because the forms make them so. */
export interface ProgramLocation {
  readonly streetAddress: string | null
  readonly city: string | null
  readonly region: string | null
  readonly postalCode: string | null
  readonly countyRegion: string | null
}

/**
 * Everything a program has answered, keyed by **question**.
 *
 * Where an answer is stored is the server's business. A screen only ever knows
 * the question keys the form gave it, which is what lets the wizard resume.
 */
export interface ProgramAnswers {
  readonly text: Readonly<Record<string, string>>
  readonly selections: Readonly<Record<string, readonly string[]>>
  readonly schedules: Readonly<Record<string, WeeklySchedule>>
  /** Further houses (ADR 0013), each with the id its beds are kept under. */
  readonly locations: Readonly<Record<string, readonly ProgramHouse[]>>
}

/**
 * One of a program's further houses (ADR 0013). The first house is the
 * program's own address.
 *
 * `id` is absent on a house the provider has just added and present on one
 * already saved. Saving the list back with its ids is what keeps each house's
 * bed count attached to it.
 */
export interface ProgramHouse {
  readonly id?: string
  readonly streetAddress?: string
  readonly city: string
  /** Two-letter state code. */
  readonly region: string
  readonly postalCode?: string
  readonly countyRegion?: string
}

/** A further house on the provider's own programs list, with its beds. */
export interface ProgramHouseRow {
  readonly id: string
  readonly streetAddress: string | null
  readonly city: string | null
  readonly region: string | null
  readonly seats: OrgSeats
}

/** `sun` through `sat`. */
export type Weekday = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat'

/** The week, starting Sunday — the order the server returns days in. */
export const WEEK: readonly Weekday[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

/** One schedule a program runs: the days it meets and the hours on each. */
export interface ScheduleTrack {
  readonly days: readonly Weekday[]
  /** Local wall-clock time, `HH:MM` on a 24-hour clock. */
  readonly start: string
  /** After `start`. */
  readonly end: string
}

/**
 * When a day-treatment program meets (migration `0028`).
 *
 * One or more tracks — a daytime and an evening group are two. With no tracks
 * a save clears the question.
 */
export interface WeeklySchedule {
  readonly tracks: readonly ScheduleTrack[]
  /**
   * Whether a client may attend sessions from more than one track. Required
   * once there are two tracks; the server drops it when there are fewer.
   */
  readonly tracksCombinable?: boolean
}

/**
 * One program, read back.
 *
 * `view` names the document, not the reader: `owner` is the latest version with
 * its review state, `directory` is the approved one. The three owner-only fields
 * are absent on a directory read rather than null.
 */
export interface ProgramDetail {
  readonly view: 'owner' | 'directory'
  readonly id: string
  readonly name: string
  readonly programType: string
  readonly formSource: string | null
  readonly orgId: string
  readonly location: ProgramLocation
  readonly answers: ProgramAnswers
  readonly approvalState: ApprovalState | null
  readonly version: number | null
  readonly sectionsComplete: readonly string[] | null
}

/**
 * What creating a program needs: a name and a form.
 *
 * The form is also what the program's directory type comes from, and it is the
 * one choice made before the form opens, because it decides which questions get
 * asked. It used to take a `providerType` as well — which was asked again as
 * question 2 of section 1, where it could be changed to contradict the first
 * answer, and which is not a level of care.
 */
export interface CreateProgramInput {
  readonly formSource: string
  readonly name: string
}

/**
 * One step of the form, saved.
 *
 * An absent question is left alone; an empty selection list clears one. That is
 * what lets a screen save a single field without resending the step.
 */
export interface SaveProgramSectionInput {
  readonly section: string
  readonly text?: Readonly<Record<string, string>>
  readonly selections?: Readonly<Record<string, readonly string[]>>
  /** A schedule with no tracks clears its question; an absent key leaves it alone. */
  readonly schedules?: Readonly<Record<string, WeeklySchedule>>
  /** The whole list of further houses: an empty list clears it. */
  readonly locations?: Readonly<Record<string, readonly ProgramHouse[]>>
}

/** What saving a step did. Saving never changes the approval state. */
export interface SaveProgramSectionReceipt {
  readonly approvalState: ApprovalState
  readonly version: number
  readonly section: string
  readonly sectionsComplete: readonly string[]
  /** Required questions in this step still unanswered. Reported, not refused. */
  readonly missingRequired: readonly string[]
}

/**
 * What submitting did.
 *
 * An incomplete profile is a success with a list, not an error: the request was
 * not malformed, the answer to "is this ready" is just no.
 */
export interface SubmitProgramResult {
  readonly approvalState: ApprovalState
  readonly version: number
  /** `section/question` for each one, so a screen can jump to the right step. */
  readonly missingRequired: readonly string[]
}

export interface ApiClient {
  getHome: () => Promise<HomePayload>

  getTaxonomy: () => Promise<readonly TaxonomyTerm[]>
  /** The subset of that vocabulary each search control may offer. */
  getDirectoryFilters: () => Promise<DirectoryFilters>

  /** The caller's own org: `GET /orgs/{id}` with the session's org id, owner view. */
  getOrgProfile: () => Promise<OwnOrgProfile>
  /** Any org by id. The server decides which view the caller gets. */
  getOrg: (id: string) => Promise<OrgView>
  updateOrgProfile: (input: UpdateOrgProfileInput, idempotencyKey: string) => Promise<ProfileEditReceipt>
  /**
   * Authorizes one object upload and returns a presigned URL (`ORG-02`).
   * Publishing it is a separate `updateOrgProfile` naming the returned id.
   */
  createOrgImageUpload: (
    contentType: OrgImageContentType,
    idempotencyKey: string
  ) => Promise<OrgImageUpload>
  /**
   * Sends the bytes straight to the object store, not through the API.
   *
   * Separate from `createOrgImageUpload` because it is the one call in this
   * client that does not go to Match Point: it carries no session, no
   * idempotency key and no problem-document error shape, and a failure here
   * means S3 refused the signature rather than that the product said no.
   */
  uploadOrgImage: (upload: OrgImageUpload, file: Blob) => Promise<void>

  listPendingAccounts: (query: AccountQueueQuery) => Promise<Page<PendingAccount>>
  /** The other staff queue: submitted profile versions awaiting a decision. */
  listPendingProfiles: (query: ProfileQueueQuery) => Promise<Page<PendingProfile>>
  decideAccount: (orgId: string, input: AccountDecisionInput, idempotencyKey: string) => Promise<AccountDecisionResult>
  decideOrgProfile: (orgId: string, input: ProfileDecisionInput, idempotencyKey: string) => Promise<ProfileDecisionResult>
  /** `DIR-01`: filters and a sort over every listed program. No score. */
  searchDirectory: (query: DirectoryQuery) => Promise<DirectoryResults>

  /** The third staff queue: submitted program listings awaiting a decision. */
  listPendingPrograms: (query: ProgramQueueQuery) => Promise<Page<PendingProgram>>
  decideProgram: (programId: string, input: ProgramDecisionInput, idempotencyKey: string) => Promise<ProgramDecisionResult>

  /** `NTF-04`: the caller's own approval-notification choices. */
  getStaffNotificationPreferences: () => Promise<StaffNotificationPreferences>
  putStaffNotificationPreferences: (
    preferences: StaffNotificationPreferences,
    idempotencyKey: string
  ) => Promise<StaffNotificationPreferences>

  /** `ADM-08`: the forms whose option lists staff can edit. */
  listManagedForms: () => Promise<readonly ManagedForm[]>
  getManagedQuestionOptions: (source: string, key: string) => Promise<ManagedQuestionOptions>
  addFormOption: (
    source: string,
    key: string,
    input: AddFormOptionInput,
    idempotencyKey: string
  ) => Promise<ManagedQuestionOptions>
  updateFormOption: (
    source: string,
    key: string,
    taxonomyId: string,
    input: UpdateFormOptionInput,
    idempotencyKey: string
  ) => Promise<ManagedQuestionOptions>
  /** A full list sets the order; `null` returns the question to the alphabet. */
  reorderFormOptions: (
    source: string,
    key: string,
    taxonomyIds: readonly string[] | null,
    idempotencyKey: string
  ) => Promise<ManagedQuestionOptions>

  /** The onboarding form for one program type, served from the database. */
  getProgramForm: (source: string) => Promise<ProgramFormDefinition>
  /** Every program the calling organization runs, oldest first. */
  listPrograms: () => Promise<readonly ProgramListRow[]>
  createProgram: (input: CreateProgramInput, idempotencyKey: string) => Promise<ProgramListRow>
  /**
   * One program. The server decides which view the caller gets, except that the
   * owner may ask for `directory` to see what clinicians see while an edit is in
   * review. Ignored for everybody else.
   */
  getProgram: (id: string, view?: 'owner' | 'directory') => Promise<ProgramDetail>
  saveProgramSection: (
    id: string,
    input: SaveProgramSectionInput,
    idempotencyKey: string
  ) => Promise<SaveProgramSectionReceipt>
  submitProgram: (id: string, idempotencyKey: string) => Promise<SubmitProgramResult>
  /** `PRG-11`: applied at once to every version, and moves no approval state. */
  updateProgramSeats: (id: string, input: ProgramSeatsInput, idempotencyKey: string) => Promise<ProgramSeatsReceipt>
}

/**
 * Open beds summed over the programs that have reported a count.
 *
 * A marker, never a zero, when none has (I-6): "no program has told us" and
 * "every program is full" are different facts. Programs with no count are left
 * out of the sum rather than counted as zero, for the same reason.
 */
export function openBedsAcross (programs: readonly ProgramListRow[]): Reported<number> {
  // Every house, first and further (ADR 0013): a program's open beds are all of
  // its houses' open beds.
  const counts = programs.flatMap(program =>
    [program.seats, ...program.houses.map(house => house.seats)]
      .flatMap(seats => seats.open === null ? [] : [seats.open]))
  return counts.length === 0 ? unreported() : reported(counts.reduce((sum, open) => sum + open, 0))
}
